import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import RelatedContent from '@/components/RelatedContent';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, Search, Tag, ArrowRight, BookOpen, Eye, Heart, TrendingUp, Award, Star } from 'lucide-react';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Enhanced blog posts with rich content
  const blogPosts = [
  {
    id: 1,
    title: "Guía Completa de Domótica para Principiantes",
    excerpt: "Descubre cómo transformar tu hogar en una casa inteligente con esta guía paso a paso.",
    content: `La domótica ha revolucionado la forma en que interactuamos con nuestros hogares. En esta guía completa, te mostramos cómo dar tus primeros pasos.

## ¿Qué es la Domótica?

La domótica es el conjunto de tecnologías aplicadas al control y la automatización inteligente de la vivienda. Permite una gestión eficiente del uso de la energía, aporta seguridad, bienestar y comunicación entre el usuario y el sistema.

## Componentes Básicos

- **Sensores**: Detectan cambios en el ambiente
- **Actuadores**: Ejecutan las acciones programadas
- **Sistema de Control**: Procesa la información y toma decisiones
- **Interfaces**: Permiten la interacción con el usuario

## Primeros Pasos

1. **Evaluación del hogar**: Identifica las necesidades específicas
2. **Presupuesto**: Define cuánto estás dispuesto a invertir
3. **Selección de tecnología**: Elige el protocolo adecuado
4. **Instalación gradual**: Comienza con sistemas básicos`,
    author: "Nathan Automatiza",
    date: "2024-01-15",
    category: "Guías",
    tags: ["domótica", "principiantes", "smart home"],
    readTime: "5 min",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=400&fit=crop&crop=center",
    views: 1250,
    likes: 89,
    featured: true
  },
  {
    id: 2,
    title: "HomeSeer vs Competencia: Comparativa Completa",
    excerpt: "Análisis detallado de por qué HomeSeer supera a la competencia en funcionalidad y precio.",
    content: `En el mundo de la domótica, elegir la plataforma correcta es crucial para el éxito de tu proyecto. HomeSeer destaca por múltiples razones.

## Análisis de Características

HomeSeer ofrece una combinación única de potencia, flexibilidad y facilidad de uso que lo distingue de la competencia.

### Ventajas de HomeSeer:
- **Interfaz intuitiva**: Fácil de usar para principiantes
- **Potencia profesional**: Capacidades avanzadas para expertos
- **Precio competitivo**: Mejor relación calidad-precio
- **Soporte técnico**: Excelente atención al cliente

### Comparativa con la Competencia:
- **Vs. Control4**: Más asequible y flexible
- **Vs. Crestron**: Instalación más sencilla
- **Vs. Savant**: Mejor documentación y soporte`,
    author: "Nathan Automatiza",
    date: "2024-01-10",
    category: "Comparativas",
    tags: ["homeseer", "comparativa", "tecnología"],
    readTime: "7 min",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=400&fit=crop&crop=center",
    views: 2340,
    likes: 156,
    featured: false
  },
  {
    id: 3,
    title: "Automatización de Cine en Casa: Proyecto Completo",
    excerpt: "Cómo crear la experiencia de cine perfecta con automatización inteligente.",
    content: `Un sistema de cine en casa automatizado puede transformar completamente tu experiencia de entretenimiento. Te mostramos cómo implementarlo.

## Componentes del Sistema

### Audio y Video
- **Procesador de audio**: Manejo de sonido multicanal
- **Matriz de video**: Distribución de señales
- **Proyector**: Imagen de alta calidad
- **Pantalla motorizada**: Control automático

### Iluminación
- **Luces LED**: Ambientación personalizable
- **Dimmers**: Control de intensidad
- **Escenas programadas**: Diferentes ambientes

### Control Ambiental
- **Cortinas motorizadas**: Oscurecimiento automático
- **Climatización**: Temperatura ideal
- **Ventilación**: Aire puro durante las sesiones`,
    author: "Nathan Automatiza",
    date: "2024-01-05",
    category: "Proyectos",
    tags: ["cine", "entretenimiento", "automatización"],
    readTime: "6 min",
    image: "https://images.unsplash.com/photo-1489844097929-c8d5b91c456e?w=800&h=400&fit=crop&crop=center",
    views: 1890,
    likes: 134,
    featured: false
  },
  {
    id: 4,
    title: "Fibaro Swipe: Revolucionando el Control Gestual",
    excerpt: "Descubre cómo el control gestual está cambiando la forma de interactuar con el hogar.",
    content: `El Fibaro Swipe representa una nueva era en el control domótico, ofreciendo una interfaz completamente intuitiva y natural.

## Características Innovadoras

### Gestos Reconocidos
- **Swipe Up**: Subir persianas o aumentar iluminación
- **Swipe Down**: Bajar persianas o disminuir luces
- **Swipe Left/Right**: Cambiar escenas o modos
- **Circular**: Activar/desactivar sistemas

### Ventajas del Control Gestual
- **Intuitividad**: Gestos naturales
- **Higiene**: Sin contacto físico
- **Velocidad**: Respuesta instantánea
- **Accesibilidad**: Fácil para todas las edades

### Casos de Uso Prácticos
- **Cocina**: Control sin tocar con manos sucias
- **Dormitorio**: Ajustes nocturnos sin buscar interruptores
- **Sala de estar**: Control elegante durante reuniones`,
    author: "Nathan Automatiza",
    date: "2024-01-03",
    category: "Productos",
    tags: ["fibaro", "gestos", "innovación"],
    readTime: "4 min",
    image: "https://images.unsplash.com/photo-1616941424163-e0c65236a7ca?w=800&h=400&fit=crop&crop=center",
    views: 980,
    likes: 67,
    featured: false
  },
  {
    id: 5,
    title: "Ahorro Energético con Domótica: Casos Reales",
    excerpt: "Descubre cómo la domótica puede reducir tu factura eléctrica hasta un 40%.",
    content: `Los sistemas domóticos no solo aportan comodidad, sino que también pueden generar ahorros significativos en tu factura energética.

## Estrategias de Ahorro

### Gestión Inteligente de Iluminación
- **Sensores de presencia**: Luces solo cuando es necesario
- **Regulación automática**: Ajuste según luz natural
- **Horarios programados**: Optimización de uso

### Control de Climatización
- **Termostatos inteligentes**: Temperatura óptima
- **Zonificación**: Climatización por áreas
- **Programación**: Horarios de uso eficientes

### Monitoreo de Consumo
- **Medidores inteligentes**: Seguimiento en tiempo real
- **Alertas**: Notificaciones de consumo excesivo
- **Reportes**: Análisis de patrones de uso`,
    author: "Nathan Automatiza",
    date: "2024-01-01",
    category: "Eficiencia",
    tags: ["ahorro", "energía", "sostenibilidad"],
    readTime: "8 min",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&h=400&fit=crop&crop=center",
    views: 3450,
    likes: 234,
    featured: false
  }];


  const categories = ["Todas", "Guías", "Comparativas", "Proyectos", "Productos", "Eficiencia"];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === "Todas" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find((post) => post.featured) || blogPosts[0];
  const regularPosts = blogPosts.filter((post) => !post.featured);

  return (
    <div className="min-h-screen bg-gray-50" data-id="7039ogq5p" data-path="src/pages/BlogPage.tsx">
      <Header data-id="7km6z1eie" data-path="src/pages/BlogPage.tsx" />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20" data-id="g4t2h47ay" data-path="src/pages/BlogPage.tsx">
        <div className="container mx-auto px-4" data-id="oq605we74" data-path="src/pages/BlogPage.tsx">
          <div className="max-w-4xl mx-auto text-center" data-id="3nc2j31k5" data-path="src/pages/BlogPage.tsx">
            <h1 className="text-4xl md:text-6xl font-bold mb-6" data-id="7l81ojcft" data-path="src/pages/BlogPage.tsx">
              Blog de Automatiza.me
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8" data-id="r2878olds" data-path="src/pages/BlogPage.tsx">
              Descubre las últimas tendencias en domótica, guías expertas y casos de éxito
            </p>
            <div className="flex items-center justify-center space-x-4 text-blue-100" data-id="gknfn3wnq" data-path="src/pages/BlogPage.tsx">
              <div className="flex items-center space-x-2" data-id="nyj40dvnb" data-path="src/pages/BlogPage.tsx">
                <BookOpen className="w-5 h-5" data-id="84zwv28zu" data-path="src/pages/BlogPage.tsx" />
                <span data-id="ww0yf5fbu" data-path="src/pages/BlogPage.tsx">Guías Expertas</span>
              </div>
              <div className="w-px h-6 bg-blue-300" data-id="4iaet64oz" data-path="src/pages/BlogPage.tsx"></div>
              <div className="flex items-center space-x-2" data-id="px7czah3x" data-path="src/pages/BlogPage.tsx">
                <User className="w-5 h-5" data-id="rd98ncjwl" data-path="src/pages/BlogPage.tsx" />
                <span data-id="4n0ce5g77" data-path="src/pages/BlogPage.tsx">25+ años experiencia</span>
              </div>
              <div className="w-px h-6 bg-blue-300" data-id="cgz1tn6f4" data-path="src/pages/BlogPage.tsx"></div>
              <div className="flex items-center space-x-2" data-id="uon20egmf" data-path="src/pages/BlogPage.tsx">
                <Award className="w-5 h-5" data-id="z03qmidd0" data-path="src/pages/BlogPage.tsx" />
                <span data-id="9sjb6yjq3" data-path="src/pages/BlogPage.tsx">Casos Reales</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16" data-id="jchw4pha1" data-path="src/pages/BlogPage.tsx">
        <Breadcrumbs items={[{ label: 'Blog' }]} data-id="gkoucdxe2" data-path="src/pages/BlogPage.tsx" />
        
        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-12" data-id="a6iavjxbk" data-path="src/pages/BlogPage.tsx">
          <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="sssnpaxwu" data-path="src/pages/BlogPage.tsx">
            <div className="flex-1 relative" data-id="gscpzoakl" data-path="src/pages/BlogPage.tsx">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="jh4oat962" data-path="src/pages/BlogPage.tsx" />
              <Input
                placeholder="Buscar artículos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" data-id="nxy8t1onc" data-path="src/pages/BlogPage.tsx" />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2" data-id="enaqf88d5" data-path="src/pages/BlogPage.tsx">
            {categories.map((category) =>
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ?
              "bg-blue-600 hover:bg-blue-700" :
              "hover:bg-blue-50 hover:border-blue-300"
              } data-id="c0lyvk99r" data-path="src/pages/BlogPage.tsx">
                {category}
              </Button>
            )}
          </div>
        </div>

        {/* Featured Post */}
        <div className="max-w-6xl mx-auto mb-16" data-id="5mrilpkqq" data-path="src/pages/BlogPage.tsx">
          <div className="flex items-center justify-between mb-8" data-id="j5v8e296x" data-path="src/pages/BlogPage.tsx">
            <h2 className="text-3xl font-bold text-gray-800" data-id="d68vm92cl" data-path="src/pages/BlogPage.tsx">Artículo Destacado</h2>
            <Badge variant="outline" className="border-yellow-500 text-yellow-700" data-id="pbmlmmwvk" data-path="src/pages/BlogPage.tsx">
              <Star className="w-4 h-4 mr-1" data-id="xc6vr9ahx" data-path="src/pages/BlogPage.tsx" />
              Destacado
            </Badge>
          </div>
          
          <Card className="overflow-hidden shadow-2xl" data-id="rlvvwwfed" data-path="src/pages/BlogPage.tsx">
            <div className="md:flex" data-id="jihn6e0g3" data-path="src/pages/BlogPage.tsx">
              <div className="md:w-1/2" data-id="uo65bap53" data-path="src/pages/BlogPage.tsx">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-64 md:h-full object-cover" data-id="61qhkf9bj" data-path="src/pages/BlogPage.tsx" />
              </div>
              <div className="md:w-1/2 p-8" data-id="84ux1btj1" data-path="src/pages/BlogPage.tsx">
                <div className="flex items-center space-x-4 mb-4" data-id="pugl7k331" data-path="src/pages/BlogPage.tsx">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800" data-id="f37x7wcns" data-path="src/pages/BlogPage.tsx">
                    {featuredPost.category}
                  </Badge>
                  <div className="flex items-center space-x-2 text-gray-500 text-sm" data-id="v87xwnqop" data-path="src/pages/BlogPage.tsx">
                    <Calendar className="w-4 h-4" data-id="1x7hwp0gs" data-path="src/pages/BlogPage.tsx" />
                    <span data-id="tp5asx1rk" data-path="src/pages/BlogPage.tsx">{new Date(featuredPost.date).toLocaleDateString('es-ES')}</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4" data-id="6pckrx9nv" data-path="src/pages/BlogPage.tsx">{featuredPost.title}</h3>
                <p className="text-gray-600 mb-6 line-clamp-3" data-id="56oyv4aen" data-path="src/pages/BlogPage.tsx">{featuredPost.excerpt}</p>
                <div className="flex items-center justify-between" data-id="ixd19z6o7" data-path="src/pages/BlogPage.tsx">
                  <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="xv8fgc162" data-path="src/pages/BlogPage.tsx">
                    <div className="flex items-center space-x-1" data-id="sj8bjzu9t" data-path="src/pages/BlogPage.tsx">
                      <Eye className="w-4 h-4" data-id="1cfifmrrh" data-path="src/pages/BlogPage.tsx" />
                      <span data-id="zzu60dkch" data-path="src/pages/BlogPage.tsx">{featuredPost.views.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1" data-id="rwnwdsq9v" data-path="src/pages/BlogPage.tsx">
                      <Heart className="w-4 h-4" data-id="jrmp5h03r" data-path="src/pages/BlogPage.tsx" />
                      <span data-id="j649485hn" data-path="src/pages/BlogPage.tsx">{featuredPost.likes}</span>
                    </div>
                    <div className="flex items-center space-x-1" data-id="wodo4rqt7" data-path="src/pages/BlogPage.tsx">
                      <Clock className="w-4 h-4" data-id="ztk3phr5m" data-path="src/pages/BlogPage.tsx" />
                      <span data-id="t3f27k2fh" data-path="src/pages/BlogPage.tsx">{featuredPost.readTime}</span>
                    </div>
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-id="0qgt4necg" data-path="src/pages/BlogPage.tsx">
                    Leer más
                    <ArrowRight className="w-4 h-4 ml-2" data-id="6cmgd6nv0" data-path="src/pages/BlogPage.tsx" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Stats Section */}
        <div className="max-w-4xl mx-auto mb-16" data-id="m5rc1rt1t" data-path="src/pages/BlogPage.tsx">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="izy3dz912" data-path="src/pages/BlogPage.tsx">
            <Card className="text-center p-6" data-id="6gyj5wrat" data-path="src/pages/BlogPage.tsx">
              <div className="flex items-center justify-center mb-4" data-id="yhxk8mrps" data-path="src/pages/BlogPage.tsx">
                <div className="bg-blue-100 p-3 rounded-full" data-id="6agdlxoh7" data-path="src/pages/BlogPage.tsx">
                  <BookOpen className="w-8 h-8 text-blue-600" data-id="z63qq7qfi" data-path="src/pages/BlogPage.tsx" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="gju62wnso" data-path="src/pages/BlogPage.tsx">50+</h3>
              <p className="text-gray-600" data-id="m8f70hhzz" data-path="src/pages/BlogPage.tsx">Artículos Publicados</p>
            </Card>
            
            <Card className="text-center p-6" data-id="glzmtl7zf" data-path="src/pages/BlogPage.tsx">
              <div className="flex items-center justify-center mb-4" data-id="9vdfe7s7h" data-path="src/pages/BlogPage.tsx">
                <div className="bg-green-100 p-3 rounded-full" data-id="j7j0dc6ds" data-path="src/pages/BlogPage.tsx">
                  <TrendingUp className="w-8 h-8 text-green-600" data-id="e7hedtdyl" data-path="src/pages/BlogPage.tsx" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="uzqu5m1f8" data-path="src/pages/BlogPage.tsx">100K+</h3>
              <p className="text-gray-600" data-id="4bec2x4xg" data-path="src/pages/BlogPage.tsx">Lectores Mensuales</p>
            </Card>
            
            <Card className="text-center p-6" data-id="bts5p1mpg" data-path="src/pages/BlogPage.tsx">
              <div className="flex items-center justify-center mb-4" data-id="wo9k3vz16" data-path="src/pages/BlogPage.tsx">
                <div className="bg-purple-100 p-3 rounded-full" data-id="0xjjdjrrt" data-path="src/pages/BlogPage.tsx">
                  <Award className="w-8 h-8 text-purple-600" data-id="on6kgynr6" data-path="src/pages/BlogPage.tsx" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="bwgs3k88x" data-path="src/pages/BlogPage.tsx">25+</h3>
              <p className="text-gray-600" data-id="hvrp4b375" data-path="src/pages/BlogPage.tsx">Años de Experiencia</p>
            </Card>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="max-w-6xl mx-auto" data-id="4eiv5rurs" data-path="src/pages/BlogPage.tsx">
          <div className="flex items-center justify-between mb-8" data-id="d8wacbp17" data-path="src/pages/BlogPage.tsx">
            <h2 className="text-3xl font-bold text-gray-800" data-id="iciu9akmy" data-path="src/pages/BlogPage.tsx">Todos los Artículos</h2>
            <div className="text-gray-500" data-id="qlzhla5ji" data-path="src/pages/BlogPage.tsx">
              {filteredPosts.length} artículo{filteredPosts.length !== 1 ? 's' : ''} encontrado{filteredPosts.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="97xzb6yl1" data-path="src/pages/BlogPage.tsx">
            {filteredPosts.filter((post) => !post.featured).map((post) =>
            <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300" data-id="9yy7u0fsn" data-path="src/pages/BlogPage.tsx">
                <div className="aspect-video overflow-hidden" data-id="0fc884sel" data-path="src/pages/BlogPage.tsx">
                  <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="nwylvna3r" data-path="src/pages/BlogPage.tsx" />
                </div>
                <CardContent className="p-6" data-id="xdodz9az1" data-path="src/pages/BlogPage.tsx">
                  <div className="flex items-center space-x-4 mb-3" data-id="fks2510b4" data-path="src/pages/BlogPage.tsx">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800" data-id="0c7pr76c9" data-path="src/pages/BlogPage.tsx">
                      {post.category}
                    </Badge>
                    <div className="flex items-center space-x-2 text-gray-500 text-sm" data-id="3b4ftliwb" data-path="src/pages/BlogPage.tsx">
                      <Calendar className="w-4 h-4" data-id="jnc1us3db" data-path="src/pages/BlogPage.tsx" />
                      <span data-id="jqcxrjuy8" data-path="src/pages/BlogPage.tsx">{new Date(post.date).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2" data-id="sfgszoo35" data-path="src/pages/BlogPage.tsx">{post.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-3" data-id="r759y9mav" data-path="src/pages/BlogPage.tsx">{post.excerpt}</p>
                  <div className="flex items-center justify-between" data-id="atfcahhbz" data-path="src/pages/BlogPage.tsx">
                    <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="a89luyulu" data-path="src/pages/BlogPage.tsx">
                      <div className="flex items-center space-x-1" data-id="akxco89wl" data-path="src/pages/BlogPage.tsx">
                        <Eye className="w-4 h-4" data-id="gj63o28ia" data-path="src/pages/BlogPage.tsx" />
                        <span data-id="yqbozperi" data-path="src/pages/BlogPage.tsx">{post.views.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center space-x-1" data-id="vm1p6z59e" data-path="src/pages/BlogPage.tsx">
                        <Clock className="w-4 h-4" data-id="h3dhsqf8x" data-path="src/pages/BlogPage.tsx" />
                        <span data-id="1xjfd4c67" data-path="src/pages/BlogPage.tsx">{post.readTime}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="hover:bg-blue-50" data-id="3kqcgfiyz" data-path="src/pages/BlogPage.tsx">
                      Leer más
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="max-w-4xl mx-auto mt-16" data-id="2yavs9o2c" data-path="src/pages/BlogPage.tsx">
          <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white" data-id="9kjkhip4g" data-path="src/pages/BlogPage.tsx">
            <CardContent className="p-8 text-center" data-id="hinhh6ef8" data-path="src/pages/BlogPage.tsx">
              <h3 className="text-2xl font-bold mb-4" data-id="gzfyewsrj" data-path="src/pages/BlogPage.tsx">Suscríbete a nuestro Newsletter</h3>
              <p className="text-blue-100 mb-6" data-id="tu53v71gd" data-path="src/pages/BlogPage.tsx">
                Recibe las últimas novedades en domótica y automatización directamente en tu correo
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="5gahtzdzm" data-path="src/pages/BlogPage.tsx">
                <Input
                  placeholder="Tu correo electrónico"
                  className="bg-white text-gray-800 placeholder-gray-400" data-id="aywxlt3uj" data-path="src/pages/BlogPage.tsx" />
                <Button className="bg-white text-blue-600 hover:bg-gray-100" data-id="owdftojco" data-path="src/pages/BlogPage.tsx">
                  Suscribirse
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer data-id="5b4yf4s5c" data-path="src/pages/BlogPage.tsx" />
    </div>);

};

export default BlogPage;